import React from 'react';
import {
  HoldingSearchItemData,
  HoldingModuleId,
  HoldingSearchItemVersion,
  SearchItem,
  isHoldingSearchItem,
  createHoldingSearchItem,
  HoldingType,
} from '@benzinga/search-modules';
import {
  SearchModule,
  AutocompleteModule,
  TagModule,
  ActionModule,
  ClipboardModule,
  isNoCommaString,
  QueryScore,
} from './Modules';
import { ResultDescription, ResultHead } from '../Expression/ExpressionSearch';
import { SessionContext } from '@benzinga/session-context';
import { HoldingsManager } from '@benzinga/data-manager-holdings';
import { SearchContext, SearchContextType } from '../context';
import { FaHandHoldingUsd } from 'react-icons/fa';
import styled from 'styled-components';
import { WidgetContext, WidgetId } from '@benzinga/widget-tools';
import Styles from '../styles/styles.module.scss';

export interface HoldingSearchModule extends SearchModule<HoldingSearchItemData> {
  id: typeof HoldingModuleId;
  autocomplete: AutocompleteModule<HoldingSearchItemData>;
}

const HoldingDropdown: (label: string) => React.FC<{ isSelected: boolean; isLocked?: boolean }> =
  (label: string) => props => {
    return (
      <>
        <ResultHead $isSelected={props.isSelected} className={Styles.resultHead}>
          {'Holdings of'}
        </ResultHead>
        <ResultDescription $isSelected={props.isSelected} className={Styles.resultDescription}>
          {label}
        </ResultDescription>
      </>
    );
  };

export const createHoldingAutocompleteModule = (
  holdingsManager: HoldingsManager,
  searchContext: SearchContextType,
  widgetId?: WidgetId,
): AutocompleteModule<HoldingSearchItemData> => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isHoldingSearchItem(item)) return undefined;
      const holding = holdingsManager.getCachedHoldingByName(item.data.holdingName);
      return {
        component: HoldingDropdown(holding?.holdingName ?? 'HOLDING NOT FOUND'),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: HoldingSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isHoldingSearchItem(item)) return undefined;
      return item;
    },

    query: (text: string, limit = 10) => {
      const holdings = holdingsManager.getHoldingsCached();
      const recentlyUsed = searchContext
        .getRecentModuleItem(widgetId ?? '')
        .filter(isHoldingSearchItem)
        .map<[SearchItem<HoldingSearchItemData>, QueryScore]>(item => [item, QueryScore.Priority]);

      const holdingItems = holdings.map<[SearchItem<HoldingSearchItemData>, QueryScore]>(holding => [
        createHoldingSearchItem(holding.holdingName, HoldingType.Index),
        QueryScore.HistoryMatch,
      ]);

      const uniqueHoldings = new Map<string, [SearchItem<HoldingSearchItemData>, QueryScore]>();

      [...recentlyUsed, ...holdingItems].forEach(item => {
        const name = item[0].data.holdingName;
        if (!uniqueHoldings.has(name)) {
          uniqueHoldings.set(name, item);
        }
      });

      let results = Array.from(uniqueHoldings.values());

      if (text) {
        const lower = text.toLowerCase();
        results = results.filter(([item]) => item.data.holdingName.toLowerCase().includes(lower));
      }

      return {
        cancel: () => [],
        promise: Promise.resolve({
          canceled: false,
          ok: results.slice(0, limit),
        }),
      };
    },
  };
};

export const createHoldingClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isHoldingSearchItem(token)) return undefined;
      return [token];
    },
    onCopyString: token => {
      if (!isHoldingSearchItem(token)) return undefined;
      return isNoCommaString(token.data.holdingName) ? [token.data.holdingName] : undefined;
    },
    onPasteJSON: () => {
      return undefined;
    },
  };
};

export const createHoldingTagModule = (): TagModule => {
  return {
    render: item => {
      if (!isHoldingSearchItem(item)) return undefined;
      return {
        icon: <StyledHoldingIcon />,
        text: item.data.holdingName,
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isHoldingSearchItem(item)) return undefined;
      return item.data.holdingName;
    },
  };
};

const HoldingModuleItemLoader = (item: SearchItem) => {
  switch (item.version) {
    case HoldingSearchItemVersion:
    default:
      return item as SearchItem<HoldingSearchItemData>;
  }
};

export const createHoldingActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createHoldingSearchModule = (
  holdingsManager: HoldingsManager,
  search: SearchContextType,
  widgetId?: WidgetId,
): HoldingSearchModule => {
  return {
    action: createHoldingActionModule(),
    autocomplete: createHoldingAutocompleteModule(holdingsManager, search, widgetId),
    clipboard: createHoldingClipboardModule(),
    id: HoldingModuleId,
    loader: HoldingModuleItemLoader,
    tag: createHoldingTagModule(),
  };
};

export const useHoldingSearchModule = () => {
  const session = React.useContext(SessionContext);
  const search = React.useContext(SearchContext);
  const holdingsManager = session.getManager(HoldingsManager);
  const widgetContext = React.useContext(WidgetContext);
  return React.useMemo(
    () => createHoldingSearchModule(holdingsManager, search, widgetContext.widgetId),
    [holdingsManager, search, widgetContext.widgetId],
  );
};

const StyledHoldingIcon = styled(FaHandHoldingUsd)`
  padding-top: 3px;
`;
